import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Settings2 } from 'lucide-react';
import {
  MemberAvatar,
  getMemberRoleLabel,
  buildMemberBadges,
  MemberBadge,
} from '../equipeUi';
import { formatAbsenceBlock } from '../../../utils/equipePlanningUtils';
import { formatCompactHabitualHours } from '../../../utils/equipePresencesHabitualHours';
import { getMemberTodayPresence } from '../../../utils/equipePresencesToday';
import { getParisWeekDays } from '../../../utils/equipeWeekParis';

const PRESTATIONS_PREVIEW = 4;

function PresencesPrestationsList({ prestations }) {
  const [expanded, setExpanded] = useState(false);
  if (!prestations?.length) {
    return <p className="text-sm text-gray-500">Aucune prestation attribuée pour le moment.</p>;
  }
  const visible = expanded ? prestations : prestations.slice(0, PRESTATIONS_PREVIEW);
  const hidden = prestations.length - PRESTATIONS_PREVIEW;

  return (
    <div>
      <ul className="space-y-1">
        {visible.map((p) => (
          <li key={p.id} className="text-sm text-gray-800">
            <span className="text-gray-400">•</span> {p.nom}
          </li>
        ))}
      </ul>
      {!expanded && hidden > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-xs font-semibold text-violet-700 hover:underline"
        >
          + {hidden} autre{hidden > 1 ? 's' : ''} prestation{hidden > 1 ? 's' : ''}
        </button>
      ) : null}
      {expanded && prestations.length > PRESTATIONS_PREVIEW ? (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-2 text-xs font-semibold text-gray-600 hover:underline"
        >
          Réduire
        </button>
      ) : null}
    </div>
  );
}

function overlapsWeek(absence, weekDays) {
  if (!absence?.dateDebut) return false;
  const start = new Date(absence.dateDebut);
  const end = new Date(absence.dateFin || absence.dateDebut);
  const wStart = new Date(`${weekDays[0].ymd}T00:00:00`);
  const wEnd = new Date(`${weekDays[6].ymd}T23:59:59`);
  return start <= wEnd && end >= wStart;
}

export default function PresencesMemberDetail({
  member,
  prestationsCatalog = [],
  horairesConfig,
  absences = [],
  dateExceptionsByDate = null,
  weekOffset = 0,
  loading = false,
}) {
  if (loading) {
    return (
      <div className="flex h-full min-h-[280px] items-center justify-center rounded-2xl border border-gray-100 bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-violet-600" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-center">
        <p className="text-sm font-medium text-gray-700">Sélectionnez un membre</p>
        <p className="mt-1 max-w-xs text-xs text-gray-500">
          Le détail s&apos;affiche ici : rôle, prestations, horaires et absences.
        </p>
      </div>
    );
  }

  const badges = buildMemberBadges(member);
  const today = getMemberTodayPresence(member, horairesConfig, absences, dateExceptionsByDate);
  const weekDays = getParisWeekDays(weekOffset);
  const weekAbsences = (absences || []).filter((a) => overlapsWeek(a, weekDays));
  const selectedPrestationIds = member.instructeur?.prestationIds || [];
  const linkedPrestations = prestationsCatalog.filter((p) => selectedPrestationIds.includes(p.id));
  const habitualHours = formatCompactHabitualHours(horairesConfig);

  return (
    <div className="flex h-full max-h-[calc(100vh-7rem)] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 border-b border-gray-100 bg-gradient-to-br from-violet-50/70 via-white to-white px-4 py-4">
        <div className="flex items-start gap-3">
          <MemberAvatar member={member} size="lg" className="ring-2 ring-white shadow-md" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900">
              {member.prenom} {member.nom}
            </h2>
            {member.fonctionMetier?.trim() ? (
              <p className="text-sm text-gray-600">{member.fonctionMetier.trim()}</p>
            ) : null}
            <p className="mt-1 text-xs text-gray-500">{getMemberRoleLabel(member)}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {badges.slice(0, 3).map((b) => (
                <MemberBadge key={b.key} label={b.label} className={b.className} />
              ))}
            </div>
            <p className="mt-2 text-xs font-medium text-gray-700">
              Aujourd&apos;hui :{' '}
              <span className="text-violet-700">{today.label}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {member.isIntervenant ? (
          <>
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">Prestations</h3>
              <div className="mt-2">
                <PresencesPrestationsList prestations={linkedPrestations} />
              </div>
            </section>

            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">Horaires habituels</h3>
              <div className="mt-2 rounded-xl bg-gray-50/80 px-3 py-2.5 text-xs text-gray-700">
                {habitualHours.empty ? (
                  <p className="text-gray-500">Aucune plage habituelle renseignée.</p>
                ) : habitualHours.primary ? (
                  <p className="font-medium text-gray-800">{habitualHours.primary}</p>
                ) : null}
                {habitualHours.exceptions.length > 0 ? (
                  <ul className={`space-y-1 ${habitualHours.primary ? 'mt-2 border-t border-gray-200/80 pt-2' : ''}`}>
                    {habitualHours.exceptions.map((line) => (
                      <li key={line} className="text-gray-600">
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!habitualHours.empty ? (
                  <p className="mt-2 text-[10px] text-gray-400">Détail jour par jour dans la grille centrale.</p>
                ) : null}
              </div>
            </section>

            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Absences & indisponibilités (semaine)
              </h3>
              {weekAbsences.length === 0 ? (
                <p className="mt-2 text-xs text-gray-500">Aucune absence sur cette semaine.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {weekAbsences.map((a) => {
                    const { title, detail } = formatAbsenceBlock(a.dateDebut, a.dateFin, a.motif);
                    return (
                      <li
                        key={a.id}
                        className="rounded-xl border border-gray-100 bg-amber-50/50 px-3 py-2 text-xs text-gray-800"
                      >
                        <p className="font-semibold text-amber-900">{title}</p>
                        {detail ? <p className="mt-0.5 text-amber-800">{detail}</p> : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        ) : (
          <section className="rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-4 text-sm text-gray-600">
            Ce membre ne réalise pas de prestations. Les présences semaine concernent les professionnels
            opérationnels.
          </section>
        )}

        {(member.telephone || member.email) && (
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">Contact</h3>
            <dl className="mt-2 space-y-1 text-xs text-gray-700">
              {member.telephone ? (
                <div>
                  <dt className="text-gray-400">Téléphone</dt>
                  <dd>{member.telephone}</dd>
                </div>
              ) : null}
              {member.email ? (
                <div>
                  <dt className="text-gray-400">E-mail</dt>
                  <dd className="break-all">{member.email}</dd>
                </div>
              ) : null}
            </dl>
          </section>
        )}
      </div>

      <div className="shrink-0 border-t border-gray-100 p-3">
        <Link
          to={`/equipe?member=${encodeURIComponent(member.id)}`}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm font-semibold text-violet-800 hover:bg-violet-100"
        >
          <Settings2 className="h-4 w-4" />
          Gérer la fiche (Mon équipe)
          <ChevronRight className="h-4 w-4 opacity-60" />
        </Link>
      </div>
    </div>
  );
}
